import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { emphasize } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import React from 'react';
import AsyncSelect from 'react-select/async';
import { SearchHsCode } from 'services/openapiClient';

const PREFIX = 'AsyncChipSelect';

const classes = {
  root: `${PREFIX}-root`,
  input: `${PREFIX}-input`,
  valueContainer: `${PREFIX}-valueContainer`,
  chip: `${PREFIX}-chip`,
  chipFocused: `${PREFIX}-chipFocused`,
  placeholder: `${PREFIX}-placeholder`,
  paper: `${PREFIX}-paper`,
  divider: `${PREFIX}-divider`,
  popover: `${PREFIX}-popover`,
  popoverPaper: `${PREFIX}-popoverPaper`,
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  pointerEvents: 'none',
  [`& .${classes.popoverPaper}`]: {
    padding: theme.spacing(1),
  },
}));

const StyledAsyncSelect = styled(AsyncSelect)(({ theme }) => ({
  [`& .${classes.root}`]: {
    '& .fuse-chip-select__input': {
      color: theme.palette.text.primary,
    },
    '&.standard': {
      '& $placeholder': {},
      '& $valueContainer': {
        paddingTop: 4,
      },
    },
    '&.filled': {
      '& $placeholder': {
        left: 12,
      },
      '& $valueContainer': {
        paddingTop: 24,
        paddingLeft: 12,
      },
      '& $chip': {
        border: '1px solid rgba(0, 0, 0, 0.12)',
      },
    },
    '&.outlined': {
      '& $placeholder': {
        left: 12,
      },
      '& $valueContainer': {
        paddingLeft: 12,
        paddingTop: 12,
      },
    },
  },

  [`& .${classes.input}`]: {
    display: 'flex',
    padding: 0,
    paddingLeft: 10,
    height: 'auto',
  },

  [`& .${classes.valueContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    paddingBottom: 4,
    paddingTop: 12,
    minHeight: 40,
  },

  [`& .${classes.chip}`]: {
    margin: '4px 4px 4px 0',
  },

  [`& .${classes.chipFocused}`]: {
    backgroundColor: emphasize(
      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },

  [`& .${classes.placeholder}`]: {
    position: 'absolute',
    left: 15,
    fontSize: 16,
    margin: 0,
  },

  [`& .${classes.paper}`]: {
    position: 'absolute',
    zIndex: 2,
    marginTop: theme.spacing(),
    left: 0,
    right: 0,
  },

  [`& .${classes.divider}`]: {
    height: theme.spacing(2),
  },
}));

function NoOptionsMessage(props) {
  return (
    <Typography color="textSecondary" sx={{ p: 1 }} {...props.innerProps}>
      Type to search
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  return (
    <TextField
      fullWidth
      className={clsx(classes.root, props.selectProps?.textFieldProps?.variant)}
      required={props.selectProps?.required}
      error={props.selectProps?.error}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps?.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 600 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography color="textSecondary" className={classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography sx={{ fontSize: 16 }} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return <div className={classes.valueContainer}>{props.children}</div>;
}

function ChipLabel(props) {
  const LEN = 50;
  const { label } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!(typeof label === 'string' && label.length > LEN)) {
    return label;
  }
  return (
    <div>
      <p
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        {label.substring(0, LEN)}...
      </p>
      <StyledPopover
        id="mouse-over-popover"
        className={classes.popover}
        classes={{
          paper: classes.popoverPaper,
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography>{label}</Typography>
      </StyledPopover>
    </div>
  );
}

function MultiValue(props) {
  const handleDelete = (event) => {
    props.removeProps.onClick();
    props.removeProps.onMouseDown(event);
  };

  const labelNode = props.children;

  return (
    <Chip
      tabIndex={-1}
      label={<ChipLabel label={props.children} />}
      className={clsx(
        classes.chip,
        {
          [classes.chipFocused]: props.isFocused,
        },
        props.data.class,
      )}
      onDelete={props.isDisabled ? undefined : handleDelete}
    />
  );
}

function Menu(props) {
  return (
    <Paper square className={classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

function ChipSelect(props) {
  let searchTimer = null;
  const { onSearchOptions } = props;
  const handleOnChange = (value) => {
    if (value === null) {
      value = [];
    }
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const promiseOptions = (inputValue, callback) => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    if (inputValue.length >= 1) {
      searchTimer = setTimeout(async () => {
        const options = await onSearchOptions(inputValue);
        callback(options);
        searchTimer = null;
      }, 500);
    }
  };

  return (
    <StyledAsyncSelect
      classNamePrefix="fuse-chip-select"
      isDisabled={props.disabled}
      {...props}
      components={components}
      onChange={handleOnChange}
      loadOptions={promiseOptions}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  );
}

export default React.memo(ChipSelect);
